// Per-viewer "my team" preference, kept in localStorage.
//
// The Schedule page used to default to a hardcoded team id, which silently pointed
// at the wrong team once the season rolled over and every squad moved up an age
// group. Storing the choice per browser avoids that and is a better default anyway.
//
// Stored as a list from the outset. Parents commonly have more than one child in
// the league, and only one is shown today — but writing the list shape now means
// adding a switcher later needs no migration of anyone's saved data.
//
// The previous single-object shape is still read, so nobody loses the team they
// already picked.
//
// Every access is guarded: private-mode browsers and blocked site data can make
// localStorage throw on read as well as write.

const KEY = 'leagueapp.favouriteTeam';

// /api/teams groups by name and returns an `ids` array, because one team name
// can map to several rows — the same squad appears once per conference it plays
// in. The schedule filter wants all of them; an endpoint like /teams/{id} wants
// one. Both are kept so neither caller has to guess.
function normalise(entry) {
  if (!entry) return null;
  const ids = Array.isArray(entry.teamIds) && entry.teamIds.length
    ? entry.teamIds.map(String)
    : String(entry.teamId || '').split(',').map((s) => s.trim()).filter(Boolean);
  if (!ids.length) return null;
  return {
    teamId: ids.join(','),
    teamIds: ids,
    primaryTeamId: ids[0],
    teamName: entry.teamName || null,
    ageGroup: entry.ageGroup || null,
    divisionId: entry.divisionId != null ? String(entry.divisionId) : null,
    season: entry.season || null,
  };
}

/** Every saved team, in the order they were added. */
export function readFavouriteTeams() {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    // Current shape.
    if (parsed && Array.isArray(parsed.teams)) {
      return parsed.teams.map(normalise).filter(Boolean);
    }
    // Legacy shape: a bare object holding one team.
    const single = normalise(parsed);
    return single ? [single] : [];
  } catch (e) {
    return [];
  }
}

/**
 * The primary team, or null.
 *
 * Kept as the default export shape because callers that only care about one team
 * (the Schedule page's landing filter) should not have to know about the list.
 */
export function readFavouriteTeam() {
  return readFavouriteTeams()[0] || null;
}

function persist(teams) {
  try {
    window.localStorage.setItem(KEY, JSON.stringify({ teams }));
    return true;
  } catch (e) {
    return false;
  }
}

/** Replace the whole list with a single team. */
export function writeFavouriteTeam(entry) {
  const team = normalise(entry);
  if (!team) return false;
  return persist([team]);
}

/** Add a team, or move it to the front if it is already saved. */
export function addFavouriteTeam(entry) {
  const team = normalise(entry);
  if (!team) return false;
  const rest = readFavouriteTeams().filter((t) => t.teamId !== team.teamId);
  return persist([team, ...rest]);
}

export function removeFavouriteTeam(teamId) {
  const rest = readFavouriteTeams().filter((t) => t.teamId !== String(teamId));
  return persist(rest);
}

export function clearFavouriteTeam() {
  try {
    window.localStorage.removeItem(KEY);
    return true;
  } catch (e) {
    return false;
  }
}
